import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../lib/utils'

const typographyVariants = cva('', {
  variants: {
    // Do not try to simplify this object, it is intentionally verbose
    // So that it is easy to see what is available
    variant: {
      'H1/500': 'text-h1-500',
      'H1/400': 'text-h1-400',
      'H2/500': 'text-h2-500',
      'H2/400': 'text-h2-400',
      // Compatibility weight-600 heading variants. H1/H2 live only in the
      // pinned chat widget's Tailwind config; H3/H4 remain for chat consumers.
      // Console code uses the /500 variants throughout.
      'H1/600': 'text-h1-600',
      'H2/600': 'text-h2-600',
      'H3/600': 'text-h3-600',
      'H3/500': 'text-h3-500',
      'H3/400': 'text-h3-400',
      'H4/500': 'text-h4-500',
      'H4/600': 'text-h4-600',
      'H4/400': 'text-h4-400',
      'P/600': 'text-p-600',
      'P/500': 'text-p-500',
      'P/400': 'text-p-400',
      'PI/400': 'text-pi-400',
      'S/600': 'text-s-600',
      'S/500': 'text-s-500',
      'S/400': 'text-s-400',
      'XS/600': 'text-xs-600',
      'XS/500': 'text-xs-500',
      'XS/400': 'text-xs-400',
      'XSI/400': 'text-xsi-400',
      'XXS/400': 'text-xxs-400',
      'XXS/600': 'text-xxs-600',
      // Compatibility aliases for older consumers. New console code uses the
      // weight-500 heading names keep compact product titles visually balanced.
      'L/600': 'text-h3-500',
      'XL/700': 'text-xl-500',
      'XL/500': 'text-xl-500',
    },
  },
  defaultVariants: {
    variant: 'P/400',
  },
})

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  asChild?: boolean
  as?: string
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, as, asChild, ...props }, ref) => {
    const variantTag = variant?.split('/')[0].toLowerCase()
    const isHeading = variantTag?.startsWith('h')
    const Comp = asChild ? Slot : (as ?? (isHeading ? variantTag : 'p') ?? 'div')
    return <Comp className={cn(typographyVariants({ variant, className }))} ref={ref} {...props} />
  },
)

Typography.displayName = 'Typography'

export { Typography, typographyVariants }
